import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { analytics } from '@/analytics/core/analytics';
import { resolveButtonTracking } from '@/analytics/core/resolveButtonTracking';

import ButtonContent from './ButtonContent';
import type { ButtonBaseProps, ButtonLabelProps, LinkButtonProps } from './types';

import styles from './button.module.scss';

/** A link styled as a button: a router link, or a plain `<a>` for an external page. */
export default function LinkButton(props: ButtonBaseProps & ButtonLabelProps & LinkButtonProps) {
    const {
        to,
        target = '_self',
        external = false,
        onClick,
        variant = 'primary',
        size = 'medium',
        disabled = false,
        className,
        iconOnly = false,
    } = props;
    const isExternal = external || target === '_blank' || /^https?:\/\//.test(to);

    const shared = {
        id: props.id,
        title: props.title,
        tabIndex: disabled ? -1 : props.tabIndex,
        className: classNames(styles.button, { [styles.fullWidth]: props.fullWidth }, className),
        'data-variant': variant,
        'data-size': size,
        'data-icon-only': iconOnly,
        'data-wrap': props.wrap || undefined,
        'aria-label': props['aria-label'],
        'aria-describedby': props['aria-describedby'],
        'aria-current': props['aria-current'],
        'aria-disabled': disabled || undefined,
        'data-testid': props['data-testid'],
    };

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (disabled) {
            event.preventDefault();
            return;
        }
        // The navigation is counted as a page view. An explicit tracking id still reports
        // which control was used, so a button that navigates can become a link and keep its
        // event; the name and tooltip fallbacks a button has stay page-view only here.
        const tracking = props.trackingId
            ? resolveButtonTracking({ trackingId: props.trackingId })
            : null;
        if (tracking) {
            analytics.buttonClicked?.({ ...tracking, buttonVariant: variant });
        }
        onClick?.(event);
    };

    const content = (
        <ButtonContent icon={props.icon} iconEnd={props.iconEnd}>
            {props.children}
        </ButtonContent>
    );

    if (isExternal) {
        return (
            <a
                {...shared}
                href={to}
                target={target}
                rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                onClick={handleClick}
                onMouseEnter={props.onMouseEnter}
                onMouseLeave={props.onMouseLeave}
                onFocus={props.onFocus}
                onBlur={props.onBlur}>
                {content}
            </a>
        );
    }

    return (
        <Link
            {...shared}
            to={to}
            target={target}
            onClick={handleClick}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            onFocus={props.onFocus}
            onBlur={props.onBlur}>
            {content}
        </Link>
    );
}
